import { z } from 'zod'

// Queue item status values from backend's QueueItemStatus enum.
export const QueueItemStatusSchema = z
  .enum([
    `On hold`,
    `Waiting`,
    `InputProcessing`,
    `Running`,
    `Solved`,
    `ResultProcessing`,
    `Finished`,
    `Failed`,
    `Unsolvable`,
  ])
  .optional()
export type QueueItemStatus = z.infer<typeof QueueItemStatusSchema>

export const QueueItemSchema = z.object({
  id: z.number(),
  opt_job: z.number(),
  status: QueueItemStatusSchema,
  // Optional subtype display label. Some backend serializers omit the field
  // entirely; when present it may be null when the item has no subtype.
  subtype: z.string().nullable().optional(),
  opt_start_time: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  opt_end_time: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  created_at: z.string(),
  // Moves on any write, including writes that are not status transitions, so it
  // must never be used as a phase or life-cycle boundary.
  updated_at: z.string(),
  // When `status` was last written. Terminal queue item statuses are sticky, so
  // for a terminal item this is the end of its whole life cycle — the only such
  // signal for a failed item, whose phase stamps stay null. Rows predating the
  // field send null, and an API deployment predating it omits the key.
  status_updated_at: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  input_build_started_at: z.string().nullable(),
  input_built_at: z.string().nullable(),
  queued_at: z.string().nullable(),
  started_at: z.string().nullable(),
  build_started_at: z.string().nullable(),
  solve_started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  result_received_at: z.string().nullable(),
  result_processed_at: z.string().nullable(),
})
export type QueueItem = z.infer<typeof QueueItemSchema>
